import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  SmsService,
  type SmsTemplateDto,
  type SmsTemplateRequest,
  type TemplateTokenDto,
  type TemplateType,
} from '@/services/api';

const smsSettingsKeys = {
  getSmsTemplates: () => ['getSmsTemplates'] as const,
  getSingleSmsTemplate: (smsTemplateId: string) =>
    ['getSingleSmsTemplate', smsTemplateId] as const,
  getSmsTemplateTokens: (params: GetSmsTemplateTokensQueryParams) =>
    ['getSmsTemplateTokens', params] as const,
};

export const useGetSmsSettingsList = (
  options?: Partial<UseQueryOptions<Array<SmsTemplateDto>, AxiosError>>
) => {
  return useQuery<Array<SmsTemplateDto>, AxiosError>({
    queryKey: smsSettingsKeys.getSmsTemplates(),
    queryFn: () => SmsService.getSmsTemplate(),
    ...options,
  });
};

export const useDeleteSmsTemplateMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { templateId: string }>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { templateId: string }>({
    mutationFn: ({ templateId }) =>
      SmsService.deleteSmsTemplateById({ id: templateId }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: smsSettingsKeys.getSmsTemplates(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type GetSmsTemplateParams = {
  smsTemplateId: string;
};

export const useGetSmsTemplate = (
  params: GetSmsTemplateParams,
  options?: Partial<UseQueryOptions<SmsTemplateDto, AxiosError>>
) => {
  return useQuery<SmsTemplateDto, AxiosError>({
    queryKey: smsSettingsKeys.getSingleSmsTemplate(params.smsTemplateId),
    queryFn: () =>
      SmsService.getAllSmsTemplate(
        { id: params.smsTemplateId },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type GetSmsTemplateTokensQueryParams = {
  type: TemplateType;
};

export const useGetSmsTemplateTokens = (
  params: GetSmsTemplateTokensQueryParams,
  options?: Partial<UseQueryOptions<Array<TemplateTokenDto>, AxiosError>>
) => {
  return useQuery<Array<TemplateTokenDto>, AxiosError>({
    queryKey: smsSettingsKeys.getSmsTemplateTokens(params),
    queryFn: () =>
      SmsService.getSmsTemplateToken(params, options?.axiosRequestConfig),
    ...options,
  });
};

export const useCreateSmsTemplateMutation = (
  options?: UseMutationOptions<unknown, AxiosError, SmsTemplateRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, SmsTemplateRequest>({
    mutationFn: async (body) => {
      return SmsService.postSmsTemplate({
        body,
      });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: smsSettingsKeys.getSmsTemplates(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useEditSmsTemplateMutation = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    { templateId: string; body: SmsTemplateRequest }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    AxiosError,
    { templateId: string; body: SmsTemplateRequest }
  >({
    mutationFn: async ({ templateId, body }) => {
      return SmsService.putAllSmsTemplate({
        id: templateId,
        body,
      });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: smsSettingsKeys.getSmsTemplates(),
        }),
        queryClient.invalidateQueries({
          queryKey: smsSettingsKeys.getSingleSmsTemplate(variables.templateId),
        }),
      ]);

      options?.onSuccess?.(data, variables, context);
    },
  });
};
